import { isEmpty, omit, isNull } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { ISavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';
import { TEventPerformanceFilterKey, EventPerformanceFilterKeys } from './EventPerformanceSidebarFilters';

export const transformEventPerformanceSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TEventPerformanceFilterKey, string> => {
    let result: Record<TEventPerformanceFilterKey, string> = {} as Record<TEventPerformanceFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case EventPerformanceFilterKeys.date_range_preset:
                result[key] = context.dateRangePresetOptions.find(option => option.value === value)?.label || 'unknown';
                break;
            case EventPerformanceFilterKeys.nomination_preset:
                result[key] = context.nominationPresetOptions.find(option => option.value === value)?.label || 'unknown';
                break;
            case EventPerformanceFilterKeys.event_duration_preset:
                result[key] = context.eventDurationPresetOptions.find(option => option.value === value)?.label || 'unknown';

                break;
            case EventPerformanceFilterKeys.program:
                result[key] = value
                    .map((val: string) => {
                        const option = context.programOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TEventPerformanceFilterKey, string>;
    }

    return result;
};

export const transformEventPerformanceSavedFilterValuesFromDto = (
    filter: ISavedFilter['filter_values']
): Record<TEventPerformanceFilterKey, unknown> => {
    const result: Record<TEventPerformanceFilterKey, unknown> = {} as Record<TEventPerformanceFilterKey, unknown>;

    // Convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case EventPerformanceFilterKeys.date_range_preset:
            case EventPerformanceFilterKeys.event_duration_preset:
            case EventPerformanceFilterKeys.program:
            case EventPerformanceFilterKeys.nomination_preset:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformEventPerformanceSavedFilterValuesToDto = (
    filter: ISavedFilter['filter_values']
): Record<TEventPerformanceFilterKey, unknown> => {
    const result: Record<TEventPerformanceFilterKey, unknown> = {} as Record<TEventPerformanceFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case EventPerformanceFilterKeys.date_range_preset:
            case EventPerformanceFilterKeys.event_duration_preset:
            case EventPerformanceFilterKeys.program:
            case EventPerformanceFilterKeys.nomination_preset:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case EventPerformanceFilterKeys.date_range_preset:
                if (typeof value === 'string' && !context.dateRangePresetOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;
            case EventPerformanceFilterKeys.nomination_preset:
                if (typeof value === 'string' && !context.nominationPresetOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;
            case EventPerformanceFilterKeys.event_duration_preset:
                if (typeof value === 'string' && !context.eventDurationPresetOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;
            case EventPerformanceFilterKeys.program:
                if (Array.isArray(value)) {
                    const validOptionValues = context.programOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

function getEmptyFilterProperties(obj: Record<TEventPerformanceFilterKey, string>): TEventPerformanceFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TEventPerformanceFilterKey[];
}
